import { createPageTableSchema } from '@state/_shared/tableComponentStateSchema';
import { batchDetailsDialogStateSchema } from '@state/_shared/batchDetailsDialogStateSchema';
import { bricklinkUploadDialogStateSchema } from '@state/_shared/bricklinkUploadDialogStateSchema';

// Define the specific structure for a row in the stow inventory table
const stowInventoryRowSchema = {
    type: 'object',
    properties: {
        // Core identifier, maps to batch_item_id from handler
        id: { type: 'string', default: '' }, 
        // Properties based on handler logic and likely columns
        part_name: { type: 'string', default: '' },
        color: { type: 'string', default: '' }, // Assuming string for color name/code
        qty: { type: 'number', default: 0 },
        status: { type: 'string', default: '' }, // e.g., 'new', 'stowed', 'listed'
        is_stowed: { type: 'boolean', default: false },
        stowedAt: { type: ['string', 'null'], default: null }, // ISO Date string or null
        is_listed: { type: 'boolean', default: false },
        createdAt: { type: ['string', 'null'], default: null }, // ISO Date string when item was added to batch
        user_inventory_item_id: { type: ['number', 'null'], default: null },
        user_inventory_item_quantity: { type: ['number', 'null'], default: null },
        user_inventory_item_location_name: { type: ['string', 'null'], default: null } // Location name or null
        // Add any other relevant properties displayed or used by the table/row
    }
};

// Define the possible action states for rows in this specific table
const stowInventoryRowActions = {
    isStowing: { type: 'boolean', default: false },
    isListing: { type: 'boolean', default: false },
    isMarkingListed: { type: 'boolean', default: false },
    isExporting: { type: 'boolean', default: false }
};

// Create the table schema specific to this page
const pageTableSchema = createPageTableSchema(stowInventoryRowSchema, stowInventoryRowActions);

// Note: Removed default sorting to allow persistence to work properly
// Initial sorting can be set in the component if no persisted value exists

// Schema definition for stow inventory page state
// This file defines the structure and types of the stow inventory page state
export const stowInventoryPageStateSchema = {
    // Add headerVisible property for controlling both LoggedInHeader and StowInventoryHeader visibility
    headerVisible: {
        type: 'boolean',
        default: true,
        persist: true
    },
    selectedBatchId: {
        type: 'string',
        default: null,
        persist: true
    },
    batchSelector: {
        type: 'object',
        properties: {
            selectedBatch: {
                type: 'object',
                properties: {
                    name: { type: 'string', default: '' },
                    id: { type: 'string', default: '' }
                }
            },
            isLoading: { type: 'boolean', default: false }
        }
    },
    ui: {
        type: 'object',
        properties: {
            isLoadingBatchItems: {
                type: 'boolean',
                default: false,
                description: 'Whether batch items are currently being loaded'
            }
        }
    },
    table: pageTableSchema,
    exportRequest: {
        type: 'object',
        properties: {
            scope: { type: 'string' },
            timestamp: { type: 'number' }
        },
        default: null,
        persist: false
    },
    failedBrickStoreExportDialog: {
        type: 'object',
        properties: {
            isOpen: { 
                type: 'boolean',
                default: false
            },
            items: { 
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { 
                            type: 'string',
                            default: ''
                        },
                        reason: { 
                            type: 'string',
                            default: ''
                        }
                    }
                },
                default: []
            }
        }
    },
    selectedBatchItem: {
        type: 'object',
        properties: {
            id: { type: 'string', default: '' },
            isStowed: { type: 'boolean', default: false },
            isListed: { type: 'boolean', default: false },
            isRemoving: { type: 'boolean', default: false },
            isStowing: { type: 'boolean', default: false },
            isListing: { type: 'boolean', default: false }
        }
    },
    listWithStow: {
        type: 'boolean',
        default: false,
        persist: true,
        description: 'When enabled, batch items will be automatically listed on BrickLink when stowed'
    },
    bricklinkUploadDialog: bricklinkUploadDialogStateSchema,
    batchDetailsDialog: batchDetailsDialogStateSchema
};
